// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { Suspense, type ReactNode } from "react";
import { WAKING_UP_MESSAGE } from "../components/common/warmUp/warmUpCopy";
import { PageLoader } from "../components/layout/PageLoader";
import { useSubscriptionStatus } from "../context/SubscriptionContext";
import { SubscriptionGateScreen } from "../features/billing/components/SubscriptionGateScreen";
import { SubscriptionReadErrorScreen } from "../features/billing/components/SubscriptionReadErrorScreen";
import { isDevPreviewMode } from "../utils/devEnv";
import { FullPageFrame, FullPageLoader } from "./FullPageLoader";

export function SubscriptionGate({ children }: { children: ReactNode }) {
  const { isActive, isLoading, isWakingUp, readFailure, retry } = useSubscriptionStatus();

  if (isDevPreviewMode) {
    return <>{children}</>;
  }

  if (isLoading) {
    return isWakingUp ? <FullPageLoader message={WAKING_UP_MESSAGE} /> : <FullPageLoader />;
  }

  if (readFailure !== undefined) {
    return (
      <FullPageFrame>
        <SubscriptionReadErrorScreen failure={readFailure} onRetry={retry} />
      </FullPageFrame>
    );
  }

  if (!isActive) {
    return (
      <FullPageFrame>
        <Suspense fallback={<PageLoader />}>
          <SubscriptionGateScreen />
        </Suspense>
      </FullPageFrame>
    );
  }

  return <>{children}</>;
}
